/**
 * useTeamMembers — loads Practitioner resources from Medplum for use in
 * assignee selectors and filters.
 *
 * Returns a stable array of { id, name, reference } tuples.
 *
 * The Tasks page scopes this list with taskInboxCandidatesForViewer. This hook
 * still loads the practitioners the caller's AccessPolicy allows Medplum to return.
 */

import { useMedplum } from '@medplum/react-hooks';
import type { Practitioner } from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';
import {
  formatPractitionerPreferredLabel,
  formatPractitionerStaffLabel,
  getPractitionerOrganizationRef,
  getRole,
  getSupervisorRefs,
  type UnifyRole,
} from '@unify/fhir-model/resources/practitioner';

export interface TeamMember {
  id: string;
  name: string;
  preferredName?: string;
  reference: string;
  role?: UnifyRole;
  organizationId?: string;
  supervisorIds: string[];
}

export interface UseTeamMembersResult {
  members: TeamMember[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

function practitionerReferenceId(reference: string | undefined): string | undefined {
  return reference?.startsWith('Practitioner/') ? reference.slice('Practitioner/'.length) : undefined;
}

function practitionerToTeamMember(p: Practitioner): TeamMember {
  const name = formatPractitionerStaffLabel(p) || `Practitioner/${p.id}`;
  const preferredName = formatPractitionerPreferredLabel(p) || undefined;
  const organizationRef = getPractitionerOrganizationRef(p)?.reference;
  return {
    id: p.id!,
    name,
    preferredName,
    reference: `Practitioner/${p.id}`,
    role: getRole(p),
    organizationId: organizationRef?.startsWith('Organization/')
      ? organizationRef.slice('Organization/'.length)
      : undefined,
    supervisorIds: getSupervisorRefs(p)
      .map((ref) => practitionerReferenceId(ref.reference))
      .filter((id): id is string => Boolean(id)),
  };
}

export function useTeamMembers(): UseTeamMembersResult {
  const medplum = useMedplum();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        medplum.invalidateSearches('Practitioner');
        const practitioners = await medplum.searchResources('Practitioner', {
          active: 'true',
          _count: '200',
          _sort: 'name',
        });
        if (!cancelled) {
          setMembers(practitioners.map(practitionerToTeamMember));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[useTeamMembers] Failed to fetch practitioners:', err);
          setError(err instanceof Error ? err.message : 'Failed to load team members');
          setMembers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [medplum, tick]);

  return { members, loading, error, refresh };
}
